import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ModalWindow, useLocalizeMessage } from 'libs.nucleus';
import { DataSettings } from './data_settings.component';
import { useDataViews } from '../../hooks';
import { toSnakeCase } from '../../utils';
export const SettingsModal = ({ isOpen, onClose, onSave, dataView, isLoading = false }) => {
    const translate = useLocalizeMessage();
    const { updateDataView, isLoading: isApiLoading } = useDataViews();
    const dataSettingsRef = useRef(null);
    // Initialize items from dimensions and measures
    const initialItems = useMemo(() => {
        const items = [];
        // Add dimensions
        if (dataView.data?.dimensions) {
            Object.entries(dataView.data.dimensions).forEach(([key, dimension]) => {
                items.push({
                    id: `dimension-${key}`,
                    name: dimension.title || key,
                    type: dimension.type || 'string',
                    description: dimension.description || '',
                    category: 'Dimension',
                    originalName: key,
                });
            });
        }
        // Add measures
        if (dataView.data?.measures) {
            Object.entries(dataView.data.measures).forEach(([key, measure]) => {
                items.push({
                    id: `measure-${key}`,
                    name: measure.title || key,
                    type: measure.type || 'number',
                    description: measure.description || '',
                    category: 'Measure',
                    originalName: key,
                });
            });
        }
        return items;
    }, [dataView.data]);
    const [items, setItems] = useState(initialItems);
    // Reset items when dataView changes
    useEffect(() => {
        setItems(initialItems);
    }, [initialItems]);
    const itemsRef = useRef(items);
    useEffect(() => {
        itemsRef.current = items;
    }, [items]);
    const handleItemsChange = useCallback((updatedItems) => {
        setItems(updatedItems);
        itemsRef.current = updatedItems;
    }, []);
    const handleClose = useCallback(() => {
        setItems(initialItems);
        onClose();
    }, [initialItems, onClose]);
    const handleSave = useCallback(async () => {
        try {
            // Flush any pending changes before saving
            if (dataSettingsRef.current) {
                dataSettingsRef.current.flushPendingChanges();
                // Wait a tick for state updates to propagate
                await new Promise((resolve) => setTimeout(resolve, 0));
            }
            // Get the latest items from ref (updated by flush)
            const itemsToSave = itemsRef.current;
            // Start with existing dimensions and measures, then update/rename them
            const updatedDimensions = { ...(dataView.data?.dimensions || {}) };
            const updatedMeasures = { ...(dataView.data?.measures || {}) };
            itemsToSave.forEach((item) => {
                if (item.category === 'Dimension') {
                    const originalKey = item.originalName;
                    const dimension = dataView.data?.dimensions?.[originalKey];
                    if (dimension) {
                        const newKey = toSnakeCase(item.name);
                        // If name changed, remove old entry
                        if (newKey !== originalKey) {
                            delete updatedDimensions[originalKey];
                        }
                        // Update or add new entry with snake_cased key
                        updatedDimensions[newKey] = {
                            sql: dimension.sql,
                            type: item.type,
                            title: item.name, // Keep original name for display
                            description: item.description,
                        };
                    }
                }
                else if (item.category === 'Measure') {
                    const originalKey = item.originalName;
                    const measure = dataView.data?.measures?.[originalKey];
                    if (measure) {
                        const newKey = toSnakeCase(item.name);
                        // If name changed, remove old entry
                        if (newKey !== originalKey) {
                            delete updatedMeasures[originalKey];
                        }
                        // Update or add new entry with snake_cased key
                        updatedMeasures[newKey] = {
                            sql: measure.sql,
                            type: item.type,
                            title: item.name, // Keep original name for display
                            description: item.description,
                            format: measure.format,
                            meta: measure.meta,
                        };
                    }
                }
            });
            // Update the dataset - only update dimensions and measures
            const updatedView = await updateDataView(dataView.id, {
                data: {
                    ...dataView.data,
                    dimensions: updatedDimensions,
                    measures: updatedMeasures,
                },
            });
            onSave(updatedView);
            handleClose();
        }
        catch (error) {
            // Error already handled in hook
        }
    }, [items, dataView, updateDataView, onSave, handleClose]);
    const saveButton = {
        label: translate('Save'),
        onClick: handleSave,
        disabled: isLoading || isApiLoading,
    };
    const footerSecondaryActionButtons = [
        {
            label: translate('Cancel'),
            onClick: handleClose,
            disabled: isLoading || isApiLoading,
        },
    ];
    if (!isOpen) {
        return null;
    }
    return (_jsx(ModalWindow, { title: translate('Data settings'), subtitle: dataView.name, isOpen: true, closeWindow: handleClose, footerPrimaryActionButton: saveButton, footerSecondaryActionButtons: footerSecondaryActionButtons, width: 'full', children: _jsx(DataSettings, { ref: dataSettingsRef, items: items, onItemsChange: handleItemsChange }) }));
};
